import React, { useState } from "react";
import { ShieldAlert, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Starting a support session is now entirely server-side.
 *
 * This used to stash the admin's own JWT in localStorage, overwrite it with the
 * impersonation token, and swap them back on exit — which meant the highest
 * privilege token in the product sat in script-readable storage, twice over. The
 * session is an HttpOnly cookie now, so there is nothing to stash: the impersonate
 * endpoint replaces the access cookie, and the admin's refresh cookie (scoped to
 * /api/auth, never touched) is what identifies the real admin on the way out.
 */
export const startImpersonation = (target, writeApproved, landing) => {
  // Land where the work is. For an investor that is their studio — support that has
  // to navigate there ends up opening pages it had no reason to open.
  window.location.href = landing || "/";
};

/**
 * Always-visible marker that this is a support session viewing someone else's
 * account. Deliberately loud and non-dismissible — an invisible impersonation
 * session is how staff forget which account they're acting in.
 *
 * Driven by the server's answer (`/api/auth/me` reports `impersonated`) rather than
 * by a record the client wrote for itself. That is strictly better than the old
 * arrangement even ignoring the cookie change: a banner the client could forget to
 * draw is a support session the account holder cannot see.
 */
export default function ImpersonationBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [leaving, setLeaving] = useState(false);
  if (!user?.impersonated) return null;

  const exit = async () => {
    setLeaving(true);
    try {
      // The refresh cookie never stopped being the admin's, so refreshing mints an
      // admin access cookie over the top of the impersonation one. The server
      // audits the swap; there is no token here to put back.
      await api.refreshSession();
    } catch {
      // Refresh cookie gone or expired — there is no admin session to return to, so
      // the honest outcome is the login screen rather than a half-exited state.
      window.location.href = "/login";
      return;
    }
    localStorage.removeItem("costock_user");
    window.location.href = "/admin";
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-50 bg-amber-500 text-white px-4 py-2 flex items-center gap-3 text-sm"
    >
      <ShieldAlert size={16} className="shrink-0" />
      <span className="min-w-0 flex-1 truncate">
        {t("support.bannerViewing", { email: user.email })}
        <span className="ml-2 opacity-90">
          {user.support_write_approved ? t("support.bannerWrite") : t("support.bannerReadOnly")}
        </span>
      </span>
      <button onClick={exit} disabled={leaving} data-testid="exit-impersonation"
              className="shrink-0 inline-flex items-center gap-1.5 font-medium bg-white/20 hover:bg-white/30 disabled:opacity-60 rounded-lg px-2.5 py-1 transition-colors">
        <LogOut size={14} /> {t("support.exit")}
      </button>
    </div>
  );
}
